import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import {
  PackageCoverageLimitModel,
  PackageCoverageLimitType,
  PackageCoverageMatchModel,
  PackageCoverageMatchOptionModel,
  PackageCoverageMatchRequest,
  PackageCoverageMatchService,
  PackageCoverageMatchSubOptionModel,
  PrescriptionPackageCoverageModel,
  PrescriptionPackageCoverageWriteModel,
  V2PrescriptionCategoryModel,
} from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, catchError, concat, debounceTime, map, merge, of, startWith, switchMap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';

import { CalloutComponent } from './callout.component';
import { CheckBoxComponent } from './check-box.component';
import { OptionPickerComponent, PickerOption } from './option-picker.component';
import { PackageLimitsComponent } from './package-limits.component';
import { SpinnerComponent } from './spinner.component';
import { ToggleDirective } from './toggle.directive';

/** The package option a line is drawn against, with its chosen sub-options — named, so a summary can say what was chosen. */
export interface PackageOptionSelection {
  coverageId: string;
  optionId: string;
  optionName: string;
  subOptions: { id: string; name: string }[];
}

/** Where the member's package options stand: asked, answered, nothing to choose (no flexible package or no options), or not answered. */
export type PackageOptionsStatus = 'loading' | 'ready' | 'none' | 'error';

type MatchState = { status: 'loading' } | { status: 'ready'; match: PackageCoverageMatchModel } | { status: 'none' } | { status: 'error' };

/** The franchise percentage a package option or sub-option states among its limits. */
export function franchiseLimit(limits: PackageCoverageLimitModel[] | undefined): number | undefined {
  return limits?.find(limit => limit.type === PackageCoverageLimitType.Franchise)?.amount;
}

/** The selection as a line is written with it. */
export function toCoverageWriteModel(selection: PackageOptionSelection): PrescriptionPackageCoverageWriteModel {
  return { coverageId: selection.coverageId, options: [{ optionId: selection.optionId, subOptionIds: selection.subOptions.map(sub => sub.id) }] };
}

/** A choice as a line carries it before it is written — ids alone; the names fill in once the options are read. */
export function selectionFromWriteModel(coverage: PrescriptionPackageCoverageWriteModel | undefined): PackageOptionSelection | null {
  const option = coverage?.options?.[0];
  if (!coverage || !option) {
    return null;
  }

  return { coverageId: coverage.coverageId, optionId: option.optionId, optionName: '', subOptions: option.subOptionIds.map(id => ({ id, name: '' })) };
}

/** The choice a line was guaranteed under, read back off its stored snapshot. */
export function selectionFromSnapshot(coverage: PrescriptionPackageCoverageModel | undefined): PackageOptionSelection | null {
  const option = coverage?.options?.[0];
  if (!coverage || !option) {
    return null;
  }

  return { coverageId: coverage.id, optionId: option.id, optionName: option.name, subOptions: (option.subOptions ?? []).map(sub => ({ id: sub.id, name: sub.name })) };
}

/**
 * The member's flexible package options, asked against a line's categories and diagnosis: one option
 * to draw the line against and any of its sub-options. The ones that fit are laid out first — the backend
 * decides the fit (`isMatched`), nothing here re-derives it — and every other option stays one select away,
 * since a manager may knowingly draw the line against a neighbouring one. Where nothing fits, that is said.
 *
 * Asks again whenever the categories or the diagnosis change. A choice the new answer no longer offers is dropped; a
 * member without a flexible package gets nothing to choose from (`none`).
 */
@Component({
  selector: 'guarantees-package-option-picker',
  standalone: true,
  imports: [NgTemplateOutlet, MatIcon, TranslatePipe, CalloutComponent, CheckBoxComponent, OptionPickerComponent, PackageLimitsComponent, SpinnerComponent, ToggleDirective],
  templateUrl: './package-option-picker.component.html',
  host: { class: 'flex flex-col gap-2' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackageOptionPickerComponent {
  userId = input.required<string>();
  categories = input<V2PrescriptionCategoryModel[]>([]);
  /** The ICD-10 code of the line's diagnosis — what the options' diagnoses are matched against. */
  diagnosisCode = input<string | undefined>(undefined);
  /** The day whose package content applies; absent — now. */
  asOf = input<string | undefined>(undefined);
  /** The package the guarantee was raised under — the one a grant is checked against; `undefined` while the guarantee is not raised yet. */
  guaranteePackageId = input<string | null | undefined>(undefined);
  value = input<PackageOptionSelection | null>(null);

  valueChange = output<PackageOptionSelection | null>();
  statusChange = output<PackageOptionsStatus>();
  /** The manager changed the choice — as opposed to a choice dropped because it is no longer offered. */
  touched = output<void>();
  /** The manager ticked a sub-option — what its own limits, the franchise among them, can be read from. */
  subOptionChosen = output<PackageCoverageMatchSubOptionModel>();

  private readonly matchService = inject(PackageCoverageMatchService);
  private readonly retry$ = new Subject<void>();

  /** What the match is asked: the member, the line's categories and diagnosis, and the day that applies. */
  private readonly matchRequest = computed<PackageCoverageMatchRequest>(
    () => ({
      userId: this.userId(),
      categoryCodes: this.categories()
        .map(category => category.categoryCode)
        .sort((first, second) => first - second),
      diagnosisCodes: this.diagnosisCode() ? [this.diagnosisCode()!] : [],
      asOf: this.asOf(),
    }),
    { equal: (first, second) => JSON.stringify(first) === JSON.stringify(second) }
  );

  protected readonly state = signal<MatchState>({ status: 'loading' });

  /** Whether any option fits the line directly; while unanswered, nothing is said either way. */
  protected readonly directMatch = computed(() => {
    const state = this.state();
    return state.status !== 'ready' || fittingOptions(state.match).length > 0;
  });

  /** What fits the line, each with only its fitting sub-options. */
  protected readonly matchedOptions = computed<PackageCoverageMatchOptionModel[]>(() => {
    const state = this.state();
    return state.status === 'ready' ? fittingOptions(state.match) : [];
  });

  /** Every option that does not fit, with all its sub-options — for a manager who knows better. */
  protected readonly otherOptions = computed<PackageCoverageMatchOptionModel[]>(() => {
    const state = this.state();
    return state.status === 'ready' ? unfittingOptions(state.match) : [];
  });

  protected readonly otherPickerOptions = computed<PickerOption[]>(() => this.otherOptions().map(option => ({ value: option.id, label: option.name })));

  /** The chosen option where it was picked from the others. */
  protected readonly chosenOther = computed(() => this.otherOptions().find(option => option.id === this.value()?.optionId) ?? null);

  protected readonly hasOptions = computed(() => this.matchedOptions().length + this.otherOptions().length > 0);

  constructor() {
    // Asked again on every change of categories or diagnosis — a few quick picks settle into one call.
    merge(toObservable(this.matchRequest).pipe(debounceTime(300)), this.retry$.pipe(map(() => this.matchRequest())))
      .pipe(
        switchMap(request =>
          concat(
            of<MatchState>({ status: 'loading' }),
            this.matchService.matchForGuarantee(request, this.guaranteePackageId()).pipe(
              map<PackageCoverageMatchModel | null, MatchState>(match => (match ? { status: 'ready', match } : { status: 'none' })),
              catchError(() => of<MatchState>({ status: 'error' }))
            )
          )
        ),
        startWith<MatchState>({ status: 'loading' }),
        takeUntilDestroyed()
      )
      .subscribe(state => {
        this.state.set(state);
        this.statusChange.emit(state.status === 'ready' && offeredOptions(state.match).length === 0 ? 'none' : state.status);
        this.reconcile(state);
      });
  }

  protected retry(): void {
    this.retry$.next();
  }

  protected isSelected(optionId: string): boolean {
    return this.value()?.optionId === optionId;
  }

  protected isSubSelected(subOptionId: string): boolean {
    return !!this.value()?.subOptions.some(sub => sub.id === subOptionId);
  }

  protected select(option: PackageCoverageMatchOptionModel): void {
    const state = this.state();
    if (state.status !== 'ready' || this.isSelected(option.id)) {
      return;
    }

    // Sub-options belong to their option, so a new option starts with none of them chosen.
    this.touched.emit();
    this.valueChange.emit({ coverageId: state.match.coverageId, optionId: option.id, optionName: option.name, subOptions: [] });
  }

  protected selectOther(optionId: string): void {
    const option = this.otherOptions().find(item => item.id === optionId);
    if (option) {
      this.select(option);
    } else if (this.chosenOther()) {
      this.touched.emit();
      this.valueChange.emit(null);
    }
  }

  protected toggleSub(option: PackageCoverageMatchOptionModel, subOptionId: string): void {
    const current = this.value();
    if (!current || current.optionId !== option.id) {
      return;
    }

    const chosen = new Set(current.subOptions.map(sub => sub.id));
    const ticked = !chosen.has(subOptionId);
    if (ticked) {
      chosen.add(subOptionId);
    } else {
      chosen.delete(subOptionId);
    }

    this.touched.emit();
    this.valueChange.emit({ ...current, subOptions: option.subOptions.filter(sub => chosen.has(sub.id)).map(sub => ({ id: sub.id, name: sub.name })) });

    const sub = option.subOptions.find(item => item.id === subOptionId);
    if (ticked && sub) {
      this.subOptionChosen.emit(sub);
    }
  }

  /**
   * A choice the new answer no longer offers — another coverage version, an option that stopped fitting —
   * is dropped rather than sent, and so is any chosen sub-option that stopped fitting. Names are filled in
   * from the answer, so a choice carried in by id reads by name once the options are read.
   */
  private reconcile(state: MatchState): void {
    const current = this.value();
    if (!current || state.status === 'loading' || state.status === 'error') {
      return;
    }

    const option = state.status === 'ready' && state.match.coverageId === current.coverageId ? offeredOptions(state.match).find(item => item.id === current.optionId) : undefined;
    if (!option) {
      this.valueChange.emit(null);
      return;
    }

    const chosen = new Set(current.subOptions.map(sub => sub.id));
    const subOptions = option.subOptions.filter(sub => chosen.has(sub.id)).map(sub => ({ id: sub.id, name: sub.name }));
    if (subOptions.length !== current.subOptions.length || current.optionName !== option.name || subOptions.some((sub, i) => sub.name !== current.subOptions[i]?.name)) {
      this.valueChange.emit({ ...current, optionName: option.name, subOptions });
    }
  }
}

/** The options that fit the line, each with only its fitting sub-options. */
function fittingOptions(match: PackageCoverageMatchModel): PackageCoverageMatchOptionModel[] {
  return match.options.filter(option => option.isMatched).map(option => ({ ...option, subOptions: option.subOptions.filter(sub => sub.isMatched) }));
}

/** The options that do not fit the line, each with all its sub-options. */
function unfittingOptions(match: PackageCoverageMatchModel): PackageCoverageMatchOptionModel[] {
  return match.options.filter(option => !option.isMatched);
}

/** Everything a choice may be: the fitting options first, then the rest. */
function offeredOptions(match: PackageCoverageMatchModel): PackageCoverageMatchOptionModel[] {
  return [...fittingOptions(match), ...unfittingOptions(match)];
}
